/**
 * The image picker of an image slide (plan docs/plans/lms-plan.md 5.2 "assets
 * are uploaded by admins", 6.1, 6.2 rule 12, D10; the 18.22 gap, closed by
 * WP-74). Replaces the raw asset-id text field of the slide form: the chosen
 * image is shown (through GET /assets/:id/url, the player's own route), a new
 * image is uploaded from the device (PNG, JPEG or WebP up to 25 MB, with a
 * default alt text that the slide takes when its own alt is blank), and an
 * image already in the network's library is picked from a grid. The slide
 * keeps only the asset id (D10: images only as uploaded assets by id).
 *
 * The library is read a page at a time (24 per page) and every thumbnail asks
 * for its own presigned URL (10 minutes), cached per picker while it is
 * mounted; a URL that fails shows the filename instead, never a broken image
 * (G10).
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Image as ImageIcon, ImageOff, ImagePlus, X } from 'lucide-react';
import { outlineBtnCls, primaryBtnCls } from '../../../../ui/styles';
import { apiErrorMessage } from '../../../api/client';
import { assetFileProblem, assetSizeLabel, getAssetUrl, listAssets, uploadAsset, ASSET_ALT_MAX } from '../../../api/assets';
import type { LmsAsset } from '../../../api/assets';
import { hintCls, labelCls, microLabelCls } from './styles';

export const LIBRARY_PAGE = 24;

const ACCEPT = 'image/png,image/jpeg,image/webp';

const thumbFrameCls =
  'flex h-28 w-full items-center justify-center overflow-hidden rounded-lg border border-gray-200 bg-gray-50 dark:border-gray-700 dark:bg-gray-900';

export interface AssetPickerProps {
  /** The slide's asset id ('' when none). */
  assetId: string;
  /** The slide takes the id, and the alt text when the slide's own is blank. */
  onSelect: (asset: { id: string; alt_default: string | null }) => void;
}

type UrlState = { url: string | null; error: string | null };

/** One presigned URL per asset id, kept while the picker is mounted. */
function useAssetUrls() {
  const cache = useRef(new Map<string, Promise<UrlState>>());
  const load = useCallback((id: string): Promise<UrlState> => {
    const existing = cache.current.get(id);
    if (existing) return existing;
    const next = getAssetUrl(id)
      .then((out) => ({ url: typeof out?.url === 'string' ? out.url : null, error: null }))
      .catch((err) => ({ url: null, error: apiErrorMessage(err, 'The image could not be loaded.') }));
    cache.current.set(id, next);
    return next;
  }, []);
  const forget = useCallback((id: string) => {
    cache.current.delete(id);
  }, []);
  return { load, forget };
}

function Thumb({ id, alt, load }: { id: string; alt: string; load: (id: string) => Promise<UrlState> }) {
  const [state, setState] = useState<UrlState | null>(null);
  useEffect(() => {
    let cancelled = false;
    setState(null);
    load(id).then((s) => {
      if (!cancelled) setState(s);
    });
    return () => {
      cancelled = true;
    };
  }, [id, load]);
  if (state?.url) return <img src={state.url} alt={alt} className="max-h-full max-w-full object-contain" />;
  return (
    <div className="flex flex-col items-center gap-1 p-2 text-center" role="img" aria-label={alt}>
      <ImageOff className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
      <span className="text-[10px] text-gray-500 dark:text-gray-400">{state?.error ? 'Not loaded' : 'Loading'}</span>
    </div>
  );
}

export function AssetPicker({ assetId, onSelect }: AssetPickerProps) {
  const fileId = useId();
  const altId = useId();
  const { load, forget } = useAssetUrls();

  // Upload
  const [file, setFile] = useState<File | null>(null);
  const [altDefault, setAltDefault] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileProblem = file ? assetFileProblem(file) : null;

  // Library
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [items, setItems] = useState<LmsAsset[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [libraryError, setLibraryError] = useState<string | null>(null);

  const loadPage = useCallback(async (offset: number) => {
    setLoadingLibrary(true);
    setLibraryError(null);
    try {
      const page = await listAssets({ limit: LIBRARY_PAGE, offset });
      const next = Array.isArray(page?.items) ? page.items : [];
      setItems((prev) => (offset === 0 ? next : [...prev, ...next]));
      setHasMore(page?.page?.hasMore === true);
    } catch (err) {
      setLibraryError(apiErrorMessage(err, 'The image library could not be loaded.'));
    } finally {
      setLoadingLibrary(false);
    }
  }, []);

  useEffect(() => {
    if (libraryOpen) void loadPage(0);
  }, [libraryOpen, loadPage]);

  const upload = async () => {
    if (!file || fileProblem || uploading) return;
    setUploading(true);
    setUploadError(null);
    try {
      const asset = await uploadAsset(file, altDefault);
      forget(asset.id);
      onSelect({ id: asset.id, alt_default: asset.alt_default ?? null });
      setItems((prev) => [asset, ...prev.filter((a) => a.id !== asset.id)]);
      setFile(null);
      setAltDefault('');
    } catch (err) {
      setUploadError(apiErrorMessage(err, 'The image could not be uploaded.'));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="lms-asset-picker" data-asset-id={assetId || undefined}>
      <div className="grid gap-3 sm:grid-cols-[8rem_1fr]">
        <div className={thumbFrameCls} data-testid="lms-asset-current">
          {assetId ? (
            <Thumb id={assetId} alt="The chosen image" load={load} />
          ) : (
            <div className="flex flex-col items-center gap-1 p-2 text-center">
              <ImageOff className="h-5 w-5 text-gray-400 dark:text-gray-500" aria-hidden="true" />
              <span className="text-[10px] text-gray-500 dark:text-gray-400">No image yet</span>
            </div>
          )}
        </div>
        <div className="space-y-2">
          <div className={microLabelCls}>{assetId ? 'Chosen image' : 'Image'}</div>
          <p className={hintCls}>
            Upload a PNG, JPEG or WebP (up to 25 MB), or pick one already in the library. The slide keeps the image by its id;
            SVG is never accepted (D10).
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              className={outlineBtnCls(libraryOpen)}
              onClick={() => setLibraryOpen((v) => !v)}
              aria-expanded={libraryOpen}
              data-testid="lms-asset-library-toggle"
            >
              <ImageIcon className="h-4 w-4" aria-hidden="true" />
              {libraryOpen ? 'Hide library' : 'Choose from library'}
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid="lms-asset-upload">
        <label htmlFor={fileId} className={labelCls}>
          Upload a new image
        </label>
        <input
          id={fileId}
          type="file"
          accept={ACCEPT}
          data-testid="lms-asset-file"
          className="block w-full text-sm text-gray-700 file:mr-3 file:min-h-[44px] file:rounded-lg file:border file:border-gray-300 file:bg-white file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-gray-700 hover:file:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:file:border-gray-600 dark:file:bg-gray-700 dark:file:text-gray-200 sm:file:min-h-0"
          onChange={(e) => {
            setUploadError(null);
            setFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
          }}
        />
        {file && !fileProblem ? <p className={hintCls}>{`${file.name} · ${assetSizeLabel(file.size)}`}</p> : null}
        {file && fileProblem ? (
          <p className="text-xs text-red-600 dark:text-red-400" data-testid="lms-asset-file-error">
            {fileProblem}
          </p>
        ) : null}
        <label htmlFor={altId} className={labelCls}>
          Describe the image (becomes the alt text when the slide has none)
        </label>
        <input
          id={altId}
          data-testid="lms-asset-alt-default"
          className="min-h-[44px] w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 sm:min-h-0"
          value={altDefault}
          maxLength={ASSET_ALT_MAX}
          onChange={(e) => setAltDefault(e.target.value)}
          placeholder="A coordinator reviewing a consent form with a participant"
          autoComplete="off"
        />
        {uploadError ? (
          <p className="text-xs text-red-600 dark:text-red-400" role="alert" data-testid="lms-asset-upload-error">
            {uploadError}
          </p>
        ) : null}
        <button
          type="button"
          className={primaryBtnCls}
          disabled={!file || !!fileProblem || uploading}
          onClick={() => void upload()}
          data-testid="lms-asset-upload-button"
        >
          <ImagePlus className="h-4 w-4" aria-hidden="true" />
          {uploading ? 'Uploading' : 'Upload and use'}
        </button>
      </div>

      {libraryOpen ? (
        <div className="space-y-2 rounded-lg border border-gray-200 p-3 dark:border-gray-700" data-testid="lms-asset-library">
          <div className="flex items-center justify-between gap-2">
            <div className={microLabelCls}>Library (newest first)</div>
            <button type="button" className={outlineBtnCls()} onClick={() => setLibraryOpen(false)} aria-label="Hide library">
              <X className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {libraryError ? (
            <p className="text-xs text-red-600 dark:text-red-400" role="alert">
              {libraryError}
            </p>
          ) : null}
          {!loadingLibrary && !libraryError && items.length === 0 ? <p className={hintCls}>No images in the library yet. Upload the first one above.</p> : null}
          <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
            {items.map((asset) => {
              const chosen = asset.id === assetId;
              const size = asset.width && asset.height ? `${asset.width} x ${asset.height}` : assetSizeLabel(asset.size_bytes);
              return (
                <li key={asset.id}>
                  <button
                    type="button"
                    className={`w-full space-y-1 rounded-lg p-1 text-left transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                      chosen ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                    onClick={() => onSelect({ id: asset.id, alt_default: asset.alt_default ?? null })}
                    aria-pressed={chosen}
                    data-testid="lms-asset-item"
                    data-asset-id={asset.id}
                  >
                    <div className={thumbFrameCls}>
                      <Thumb id={asset.id} alt={asset.alt_default || asset.filename} load={load} />
                    </div>
                    <div className="truncate text-xs font-medium text-gray-900 dark:text-gray-100">{asset.filename}</div>
                    <div className="text-[10px] text-gray-500 dark:text-gray-400">{size}</div>
                  </button>
                </li>
              );
            })}
          </ul>
          {hasMore ? (
            <button type="button" className={outlineBtnCls()} disabled={loadingLibrary} onClick={() => void loadPage(items.length)} data-testid="lms-asset-more">
              {loadingLibrary ? 'Loading' : 'Show more'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default AssetPicker;
